import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import claimService from '@/services/claimService';
import { ClaimType } from '@/types';
import { CURRENCY } from '@/utils/constants';

interface CreateClaimForm {
  type: string;
  treatmentDate: string;
  providerName: string;
  amount: number;
}

const CreateClaimPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const { register, handleSubmit, formState: { isSubmitting } } = useForm<CreateClaimForm>();

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const claim = await claimService.createClaim(data);
      // Auto-submit : le document est deja joint, on soumet immediatement vers AG
      return claimService.submitClaim(claim.id);
    },
    onSuccess: (submitted) => {
      // La liste et le résumé sont en cache (staleTime 5 min) : sans invalidation,
      // la nouvelle demande n'apparaît qu'après un refresh manuel.
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['claimStats'] });
      if (submitted.agTransmitted === false) {
        toast('Demande enregistrée, mais pas encore transmise à votre gestionnaire. Elle apparaît dans votre liste avec un statut « transmission en attente ».', {
          icon: '⚠️',
          duration: 8000,
        });
      } else {
        toast.success('Demande envoyée avec succès');
      }
      navigate('/claims');
    },
    onError: (error: any) => {
      // Backend returns AppError { message, statusCode } via extractApiError.
      // The most useful case: coverage suspended/terminated → 403 with explicit message.
      const message = error?.message ?? 'Erreur lors de l\'envoi de la demande';
      toast.error(message, { duration: 6000 });
    }
  });

  const onSubmit = (data: CreateClaimForm) => {
    if (!file) {
      toast.error('Veuillez ajouter un justificatif');
      return;
    }

    const formData = new FormData();
    formData.append('type', data.type);
    formData.append('treatmentDate', data.treatmentDate);
    formData.append('providerName', data.providerName);
    formData.append('amount', data.amount.toString());
    formData.append('documents', file);

    createMutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  return (
    <AppShell title="Déposer une demande" showBack backLabel="Remboursements">
      <div className="flex-1 px-4 py-6 md:px-0 md:py-4 w-full md:max-w-2xl">
        {/* Disclaimer for non-affiliated providers */}
        <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-950/20 border-l-4 border-orange-400 rounded-r-xl">
          <p className="text-xs text-orange-800 dark:text-orange-200 leading-relaxed font-medium">
            Frais réglés chez un professionnel non affilié sans tiers payant ? 
            <br />
            <strong>Déposez vos justificatifs pour obtenir votre remboursement selon votre contrat.</strong>
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-6 flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-primary"></div>
          <div className="h-1.5 flex-1 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          {/* Section 1: Details */}
          <section className="space-y-4">
            <h2 className="text-[#111813] dark:text-white text-xl font-bold leading-tight">Détails du soin</h2>
            
            {/* Nature du soin */}
            <label className="flex flex-col w-full">
              <p className="text-[#111813] dark:text-gray-300 text-sm font-medium leading-normal pb-2">Nature du soin</p>
              <div className="relative">
                <select 
                  {...register('type', { required: true })}
                  className="form-select flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#111813] dark:text-white focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-[#dbe6df] dark:border-gray-700 bg-white dark:bg-surface-dark h-14 placeholder:text-[#61896f] p-[15px] pr-10 text-base font-normal leading-normal appearance-none"
                >
                  <option value="" disabled selected>Sélectionner un type</option>
                  <option value={ClaimType.CONSULTATION}>Consultation Médicale</option>
                  <option value={ClaimType.PHARMACY}>Pharmacie</option>
                  <option value={ClaimType.OPTICAL}>Optique</option>
                  <option value={ClaimType.DENTAL}>Soins Dentaires</option>
                  <option value={ClaimType.HOSPITAL}>Hospitalisation</option>
                  <option value={ClaimType.LAB}>Analyses (Labo)</option>
                  <option value={ClaimType.SPECIALIST}>Spécialiste</option>
                  <option value={ClaimType.OTHER}>Autre</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-[#61896f] dark:text-primary">
                  <MaterialIcon icon="expand_more" />
                </div>
              </div>
            </label>

            {/* Date & Prestataire Grid */}
            <div className="grid grid-cols-1 gap-4">
              {/* Date */}
              <label className="flex flex-col w-full">
                <p className="text-[#111813] dark:text-gray-300 text-sm font-medium leading-normal pb-2">Date du soin</p>
                <div className="relative flex w-full items-center">
                  <input 
                    type="date"
                    {...register('treatmentDate', { required: true })}
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#111813] dark:text-white focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-[#dbe6df] dark:border-gray-700 bg-white dark:bg-surface-dark h-14 placeholder:text-[#61896f] p-[15px] text-base font-normal leading-normal"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-[#61896f] dark:text-primary">
                    <MaterialIcon icon="calendar_today" />
                  </div>
                </div>
              </label>

              {/* Prestataire */}
              <label className="flex flex-col w-full">
                <p className="text-[#111813] dark:text-gray-300 text-sm font-medium leading-normal pb-2">Nom du prestataire</p>
                <input 
                  type="text"
                  placeholder="Ex: Clinique Pasteur"
                  {...register('providerName', { required: true })}
                  className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#111813] dark:text-white focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-[#dbe6df] dark:border-gray-700 bg-white dark:bg-surface-dark h-14 placeholder:text-gray-400 p-[15px] text-base font-normal leading-normal"
                />
              </label>
            </div>

            {/* Montant */}
            <label className="flex flex-col w-full">
              <p className="text-[#111813] dark:text-gray-300 text-sm font-medium leading-normal pb-2">Montant total</p>
              <div className="flex w-full flex-1 items-stretch rounded-xl overflow-hidden border border-[#dbe6df] dark:border-gray-700 bg-white dark:bg-surface-dark focus-within:ring-2 focus-within:ring-primary/50">
                <input 
                  type="number"
                  placeholder="0"
                  {...register('amount', { required: true, min: 0 })}
                  className="form-input flex w-full min-w-0 flex-1 resize-none text-[#111813] dark:text-white focus:outline-0 focus:ring-0 border-none bg-transparent h-14 placeholder:text-gray-400 p-[15px] text-base font-normal leading-normal"
                />
                <div className="flex items-center justify-center bg-gray-50 dark:bg-white/5 px-4 border-l border-[#dbe6df] dark:border-gray-700">
                  <span className="text-[#111813] dark:text-gray-300 font-medium">{CURRENCY.SYMBOL}</span>
                </div>
              </div>
            </label>
          </section>

          <div className="h-px w-full bg-gray-200 dark:bg-gray-800 my-2"></div>

          {/* Section 2: Justificatifs */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[#111813] dark:text-white text-xl font-bold leading-tight">Justificatifs</h2>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">Requis</span>
            </div>

            {/* Info Alert */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-4 flex gap-3 items-start">
              <MaterialIcon icon="info" className="text-blue-600 dark:text-blue-400 shrink-0" style={{ fontSize: 20 }} />
              <p className="text-sm text-blue-800 dark:text-blue-200 leading-snug">
                Veuillez joindre une photo nette de votre <strong>facture</strong> et de la <strong>prescription</strong> médicale. Assurez-vous que les montants sont lisibles.
              </p>
            </div>

            {/* Upload Area */}
            <label className="group relative flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#dbe6df] dark:border-gray-600 bg-white dark:bg-surface-dark p-8 text-center hover:border-primary hover:bg-gray-50 dark:hover:bg-white/5 transition-all active:scale-[0.99] cursor-pointer">
              <input 
                type="file" 
                accept="image/*,.pdf" 
                className="hidden" 
                onChange={handleFileChange} 
              />
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary group-hover:scale-110 transition-transform">
                <MaterialIcon icon="add_a_photo" style={{ fontSize: 28 }} />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-[#111813] dark:text-white">
                  {file ? file.name : 'Ajouter un document'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {file ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Prendre une photo ou choisir dans la galerie'}
                </p>
              </div>
            </label>
          </section>

          {/* Footer / Submit */}
          <div className="pt-4 pb-8 space-y-4">
            <button 
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center rounded-xl bg-primary px-4 py-4 text-base font-bold text-[#102216] shadow-sm hover:bg-[#25d360] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="w-6 h-6 border-2 border-[#102216] border-t-transparent rounded-full animate-spin" />
              ) : (
                'Soumettre la demande'
              )}
            </button>
            <div className="flex items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
              <MaterialIcon icon="lock" style={{ fontSize: 16 }} />
              <span className="text-xs font-medium">Transmission sécurisée et confidentielle</span>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  );
};

export default CreateClaimPage;


